import { ArrowRightIcon, EmptyCheckmarkIcon, FilledCheckmarkIcon } from '@va/icons';
import { useTranslate } from '@va/localization';
import { TestAttributes } from '@va/types/component';
import { TooltipWrapper, TooltipWrapperProps } from '@va/ui/tooltips';
import classNames from 'classnames';
import { forwardRef, ReactNode, Ref, useMemo } from 'react';
import { fontWeights, Paragraph, paragraphSizes, ParagraphWithTooltip } from '../typography';
import './menu-list.scss';

export type MenuListItemType = {
  id: string;
  label: string;
  onClick?: () => void;
  className?: string;
  labelClassName?: string;
  icon?: ReactNode;
  endAdornment?: ReactNode;
  disabled?: boolean;
  selected?: boolean;
  logicalOperator?: string;
  tooltip?: string;
  tooltipProps?: Partial<TooltipWrapperProps>;
  ref?: Ref<any>;
} & TestAttributes;

export const MenuListItem = forwardRef<HTMLLIElement, MenuListItemType>(
  (
    {
      id,
      'data-testid': dataTestId,
      onClick,
      icon,
      label,
      endAdornment: endAdornmentProp,
      className,
      labelClassName,
      disabled,
      selected,
      logicalOperator,
      tooltip,
      tooltipProps,
    },
    ref,
  ) => {
    const translate = useTranslate();

    const endAdornment = useMemo(() => {
      if (endAdornmentProp) return endAdornmentProp;
      if (selected === undefined) return <ArrowRightIcon className='h-2.5' color='#808080' />;

      return selected ? (
        <FilledCheckmarkIcon className='w-18px h-18px' color='#09AF5C' />
      ) : (
        <EmptyCheckmarkIcon className='w-18px h-18px' />
      );
    }, [endAdornmentProp, selected]);

    return (
      <TooltipWrapper content={tooltip} disabled={!tooltip} {...tooltipProps}>
        <div>
          <li
            id={id}
            role='option'
            aria-selected={false}
            ref={ref}
            data-testid={dataTestId}
            onClick={disabled ? undefined : onClick}
            className={classNames(
              'menu-list-item flex gap-1.5 w-full items-center rounded-9px overflow-hidden',
              {
                'cursor-not-allowed opacity-50': disabled,
                'cursor-pointer hover:bg-white-snow active:bg-white-smoke': !disabled,
                'bg-white-smoke menu-list-item--applied': selected,
              },
              className,
            )}
          >
            {logicalOperator && (
              <div className='menu-list-item__logical-operator hidden bg-gray-mercury-darker items-center w-[2.125rem] self-stretch'>
                <Paragraph size={paragraphSizes.tiny} weight={fontWeights.medium}>
                  {translate(`all.filters.combinationalOperators.${logicalOperator}`)}
                </Paragraph>
              </div>
            )}
            <div className='flex flex-1 items-center justify-between gap-3 p-2 w-full'>
              <div className='flex overflow-hidden items-center gap-2'>
                {icon}
                <ParagraphWithTooltip size={paragraphSizes.tiny} weight={fontWeights.medium} className={labelClassName}>
                  {label}
                </ParagraphWithTooltip>
              </div>
              {endAdornment}
            </div>
          </li>
        </div>
      </TooltipWrapper>
    );
  },
);
